<template>
  <section class="dept-triple dept-modern__section">
    <div class="dept-modern">
      <div class="dept-triple__grid">
        <!-- Направления -->
        <article class="dept-triple__card dept-modern__card">
          <header class="dept-triple__card-header">
            <span class="dept-triple__card-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none"><path d="M12 2l2 6h6l-5 4 2 6-5-4-5 4 2-6-5-4h6l2-6z" stroke="currentColor" stroke-width="1.2"/></svg>
            </span>
            <h3 class="dept-triple__card-title">{{ directions.heading }}</h3>
          </header>
          <ul v-if="directions.items && directions.items.length" class="dept-triple__list">
            <li v-for="(item, idx) in directions.items" :key="'d-' + idx">{{ item }}</li>
          </ul>
        </article>

        <!-- Пациенту -->
        <article class="dept-triple__card dept-modern__card">
          <header class="dept-triple__card-header">
            <span class="dept-triple__card-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none"><path d="M12 4v16M4 12h16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
            </span>
            <h3 class="dept-triple__card-title">{{ patientNeeds.heading }}</h3>
          </header>
          <ul v-if="patientNeeds.items && patientNeeds.items.length" class="dept-triple__list">
            <li v-for="(item, idx) in patientNeeds.items" :key="'p-' + idx">{{ item }}</li>
          </ul>
          <p v-if="patientNeeds.notice" class="dept-triple__notice">{{ patientNeeds.notice }}</p>
        </article>

        <!-- Документы -->
        <article class="dept-triple__card dept-modern__card">
          <header class="dept-triple__card-header">
            <span class="dept-triple__card-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none"><path d="M7 3h7l5 5v13a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1z" stroke="currentColor" stroke-width="1.5"/><path d="M14 3v5h5" stroke="currentColor" stroke-width="1.5"/></svg>
            </span>
            <h3 class="dept-triple__card-title">{{ documents.heading }}</h3>
          </header>
          <ul v-if="resolvedDocumentFiles.length" class="dept-triple__files">
            <li v-for="(file, idx) in resolvedDocumentFiles" :key="file.url + '-' + idx" class="dept-triple__file">
              <div class="dept-triple__file-info">
                <span class="dept-triple__file-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none"><path d="M7 3h7l5 5v13a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1z" stroke="currentColor" stroke-width="1.5"/></svg>
                </span>
                <div class="dept-triple__file-text">
                  <span class="dept-triple__file-title">{{ file.title }}</span>
                  <span class="dept-triple__file-meta">{{ file.format || 'PDF' }}<template v-if="file.size"> · {{ file.size }}</template></span>
                </div>
              </div>
              <a
                :href="file.url"
                class="dept-triple__file-download"
                download
                :aria-label="'Скачать ' + file.title"
              >
                <svg viewBox="0 0 24 24" fill="none" width="20" height="20" aria-hidden="true">
                  <path d="M12 3v12M7 14l5 5 5-5M5 21h14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </a>
            </li>
          </ul>
          <router-link
            v-if="documents.allDocsButton && documents.allDocsButton.text"
            :to="documents.allDocsButton.link || '#'"
            class="dept-modern__btn dept-modern__btn--outline dept-triple__all-docs"
          >
            {{ documents.allDocsButton.text }}
          </router-link>
        </article>
      </div>
    </div>
  </section>
</template>

<script>
import { resolveDocumentFile } from '@/helpers/departmentDocuments'

export default {
  name: 'DepartmentTripleCards',
  props: {
    directions: { type: Object, default: () => ({ heading: '', items: [] }) },
    patientNeeds: { type: Object, default: () => ({ heading: '', items: [], notice: '' }) },
    documents: { type: Object, default: () => ({ heading: '', files: [], allDocsButton: { text: '', link: '' } }) },
  },
  computed: {
    resolvedDocumentFiles() {
      const files = (this.documents && this.documents.files) || []
      return files.map(resolveDocumentFile).filter(Boolean)
    },
  },
};
</script>

<style scoped lang="scss">
$green: #66cc66;
$green-text: #336633;

.dept-triple__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  align-items: stretch;

  @media (max-width: 991.98px) {
    grid-template-columns: 1fr;
  }
}

.dept-triple__card {
  padding: 24px;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.dept-triple__card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.dept-triple__card-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #eaf7ea;
  color: $green;

  svg {
    width: 18px;
    height: 18px;
  }
}

.dept-triple__card-title {
  margin: 0;
  font-family: "TTDrugs", sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
  color: $green-text;
}

.dept-triple__list {
  margin: 0;
  padding: 0;
  list-style: none;
  flex: 1;

  li {
    position: relative;
    padding-left: 18px;
    margin-bottom: 10px;
    font-family: "Circe", sans-serif;
    font-size: 15px;
    line-height: 1.45;
    color: #4b5563;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 8px;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: $green;
    }
  }
}

.dept-triple__notice {
  margin: 16px 0 0;
  padding: 12px 14px;
  background: #eaf7ea;
  border-radius: 10px;
  font-family: "Circe", sans-serif;
  font-size: 14px;
  line-height: 1.4;
  color: $green-text;
}

.dept-triple__files {
  margin: 0;
  padding: 0;
  list-style: none;
  flex: 1;
}

.dept-triple__file {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #eef2ee;

  &:last-child {
    border-bottom: none;
  }
}

.dept-triple__file-info {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
}

.dept-triple__file-icon {
  flex-shrink: 0;
  color: $green;

  svg {
    width: 20px;
    height: 20px;
  }
}

.dept-triple__file-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.dept-triple__file-title {
  font-family: "Circe", sans-serif;
  font-size: 14px;
  line-height: 1.35;
  color: #2d3436;
}

.dept-triple__file-meta {
  font-family: "Circe", sans-serif;
  font-size: 12px;
  color: #9ca3af;
}

.dept-triple__file-download {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: $green;
  text-decoration: none;
  transition: background-color 0.2s;

  &:hover {
    background: #eaf7ea;
  }

  &:focus-visible {
    outline: 2px solid $green;
    outline-offset: 2px;
  }
}

.dept-triple__all-docs {
  margin-top: 16px;
  align-self: flex-start;
}
</style>
